import { ChangeDetectionStrategy, Component, Input, Output, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { PencilButtonThemeEnum } from 'src/app/standalones/components/pencil-button/pencil-button.component';
import { PaymentPlan } from 'src/app/models/payment';
import { BillingFrequency, BillingStrategy, PlanAction } from '@pncl/common-models';
import { PaymentService, PopUpPageType } from 'src/app/services/payment.service';
import { FLAGS, FlagsService } from 'src/app/services/flags.service';
import { CustomerSupportService } from 'src/app/services/customer-support.service';

@Component({
  selector: 'app-plan-card',
  templateUrl: './plan-card.component.html',
  styleUrls: ['./plan-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanCardComponent {
  @Input() plan!: PaymentPlan;
  @Input() isMiniView: boolean = false;
  @Input() currentPlan: PaymentPlan | null = null;
  @Input() isInstitutionUser: boolean = false;
  @Input() billingFrequency!: BillingFrequency;
  @Output() billingFrequencyChange = new EventEmitter<BillingFrequency>();

  protected readonly IconTypes = IconTypes;
  protected readonly PencilButtonThemeEnum = PencilButtonThemeEnum;
  protected readonly BillingStrategy = BillingStrategy;
  protected readonly BillingFrequency = BillingFrequency;
  protected readonly PlanAction = PlanAction;
  protected readonly isInteger = Number.isInteger;
  protected readonly supportAnnualPlans: boolean = false;
  protected isHovered = new BehaviorSubject<boolean>(false);

  constructor(
    protected translateService: TranslateService,
    protected paymentService: PaymentService,
    protected flagsService: FlagsService,
    protected customerSupportService: CustomerSupportService,
  ) {
    if (this.flagsService.isFlagEnabled(FLAGS.ENABLE_BILLING)) {
      this.supportAnnualPlans = <boolean>(
        this.flagsService.featureFlagsVariables.enable_billing.support_annual_plans
      );
    }
  }

  protected async initiateSubscriptionAction(): Promise<void> {
    const checkoutType = this.currentPlan
      ? PopUpPageType.SwitchSubscription
      : PopUpPageType.CreateSubscribtion;

    this.paymentService.openBufferWindow(`${checkoutType} session`);
    await this.paymentService.handleCheckout(checkoutType, this.plan, this.isInstitutionUser);
  }

  protected openIntercom() {
    this.customerSupportService.openSupport({
      showCommand: 'showNewMessage',
      target: this.translateService.instant("I'm looking to subscribe to an enterprise plan"),
    });
  }

  handleMouseEnter() {
    this.isHovered.next(true);
  }

  handleMouseLeave() {
    this.isHovered.next(false);
  }

  protected switchToAnnualBilling(): void {
    if (this.billingFrequency === BillingFrequency.Monthly) {
      this.billingFrequencyChange.emit(BillingFrequency.Annually);
    }
  }
}
